import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";

const ENTIDADES = {
  todas: "Todas",
  tour: "Tours",
  user: "Usuarios",
  reserva: "Reservas",
  salida: "Salidas",
  settings: "Configuración",
};

const ACCIONES = {
  alta: { label: "Alta", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  edicion: { label: "Edición", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  baja: { label: "Baja", cls: "bg-red-50 text-red-700 border-red-200" },
  cambio_estado: { label: "Cambio estado", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  cambio_modalidad: { label: "Cambio modalidad", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  ajuste_saldo: { label: "Ajuste saldo", cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  cambio_password: { label: "Cambio contraseña", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  merge_source: { label: "Merge canal", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

export default function AuditoriaConfig() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entidad, setEntidad] = useState("todas");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (entidad !== "todas") params.entidad = entidad;
      const { data } = await api.get("/audit", { params });
      setLogs(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [entidad]);

  useEffect(() => {
    load();
  }, [load]);

  const fmt = (ts) => {
    try {
      return new Date(ts).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });
    } catch {
      return ts;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <Select value={entidad} onValueChange={setEntidad}>
          <SelectTrigger className="w-48" data-testid="audit-entidad-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(ENTIDADES).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} data-testid="audit-refresh" className="gap-2">
          <RefreshCw className="h-4 w-4" /> Actualizar
        </Button>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Fecha</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Entidad</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Acción</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Origen</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Usuario</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center">
                    <Loader2 className="h-5 w-5 animate-spin text-slate-400 inline" />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center text-slate-400 text-sm">
                    Sin registros de auditoría todavía.
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((l) => {
                  const acc = ACCIONES[l.accion] || { label: l.accion, cls: "bg-slate-100 text-slate-600 border-slate-200" };
                  return (
                    <TableRow key={l.id} className="hover:bg-slate-50" data-testid={`audit-row-${l.id}`}>
                      <TableCell className="text-sm font-mono text-slate-600 whitespace-nowrap">{fmt(l.timestamp)}</TableCell>
                      <TableCell className="text-sm text-slate-700">{ENTIDADES[l.entidad] || l.entidad}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${acc.cls}`}>
                          {acc.label}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500 capitalize">{l.origen}</TableCell>
                      <TableCell className="text-sm text-slate-700">{l.usuario_nombre || "Sistema"}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
      <p className="text-xs text-slate-400">{logs.length} registro(s).</p>
    </div>
  );
}
